import { ROUTE_PREFIX } from 'util/Constants';

// Single source of truth for the loss vocabulary. Every option list, badge colour
// and comparison in the UI derives from these, so a backend enum change lands in
// one place. Values must match sastaticketpk/apps/losses/constants.py.

// ADM / GDS wastage is deliberately not here. An ADM arrives as an airline debit
// memo rather than as a CS observation on one order, and it gets its own
// functionality — offering it on this form would collect losses this pipeline
// cannot correctly investigate.
export const LOSS_CATEGORIES = {
  ENGINEERING: 'ENGINEERING',
  OPERATING: 'OPERATING',
  STAFF: 'STAFF',
  CS_INTERNAL: 'CS_INTERNAL',
  TEMPORARY: 'TEMPORARY',
};

export const CATEGORY_OPTIONS = [
  { value: LOSS_CATEGORIES.ENGINEERING, label: 'Engineering' },
  { value: LOSS_CATEGORIES.OPERATING, label: 'Operating' },
  { value: LOSS_CATEGORIES.STAFF, label: 'Staff' },
  { value: LOSS_CATEGORIES.CS_INTERNAL, label: 'CS Internal' },
  { value: LOSS_CATEGORIES.TEMPORARY, label: 'Temporary Loss' },
];

/**
 * What "Recovered" does, shown on hover.
 *
 * One string, because the button appears on both the register table and the
 * order screen's loss card. It used to be typed out in each, which is how the
 * two drift — and this is the only place the effect is now explained, since
 * the confirmation dialog that used to explain it was removed.
 */
export const RECOVER_TOOLTIP =
  'Zeroes the amount in the register. The record is kept, with its category and description.';

/**
 * What a temporary loss may be resolved *into*.
 *
 * Temporary is deliberately absent. Resolving a temporary loss as temporary
 * would leave it in the one state the investigator never runs on, which is the
 * outcome the resolve flow exists to end. Mirrors
 * PERMANENT_LOSS_CATEGORY_CHOICES on the backend, which re-checks it.
 */
export const PERMANENT_CATEGORY_OPTIONS = CATEGORY_OPTIONS.filter(
  (option) => option.value !== LOSS_CATEGORIES.TEMPORARY
);

/**
 * What each loss type means, shown under the dropdown once one is selected.
 *
 * The four options divide on *who or what is at fault*, and the distinction is
 * not self-evident from a one-word label — Operating and Engineering both look
 * like "something broke" until you read that one is ours and the other is not.
 * A reporter picking the wrong one sends the loss to the wrong team's register,
 * so the definition belongs on the form rather than in a document nobody has
 * open while filing.
 *
 * Wording supplied by the finance/ops owners; keep it verbatim unless they
 * change it.
 */
export const CATEGORY_DEFINITIONS = {
  [LOSS_CATEGORIES.STAFF]:
    'Losses resulting directly from manual processing errors, non-compliance, or oversight by internal personnel.',
  [LOSS_CATEGORIES.CS_INTERNAL]:
    'Losses driven by unhandled customer contacts (e.g., missed calls or emails) due to queue backlogs, high volume, or capacity constraints.',
  [LOSS_CATEGORIES.ENGINEERING]:
    'Losses caused by software bugs, API failures, system downtime, or underlying technical infrastructure outages.',
  [LOSS_CATEGORIES.OPERATING]:
    'Losses resulting from third-party vendor failures, external system disruptions, or unforeseen events beyond internal control.',
  [LOSS_CATEGORIES.TEMPORARY]:
    'Money we expect to get back — an airline credit still owed, a refund in flight, a supplier dispute. It counts in the register from today, and is not investigated until you resolve it as a permanent loss.',
};

export const LOSS_STATUSES = {
  REPORTED: 'REPORTED',
  TEMPORARY: 'TEMPORARY',
  RECOVERED: 'RECOVERED',
  QUEUED: 'QUEUED',
  INVESTIGATING: 'INVESTIGATING',
  INVESTIGATED: 'INVESTIGATED',
  APPROVED: 'APPROVED',
  NOT_INVESTIGABLE: 'NOT_INVESTIGABLE',
  FAILED: 'FAILED',
  VOIDED: 'VOIDED',
};

/**
 * Review-state wording, shared by both halves of the register.
 *
 * The ADM screen imports these rather than repeating them, and the backend
 * writes the same words into the register's Investigation Status column from
 * `INVESTIGATION_STATUS_*` in `losses/constants.py`. One report should not
 * describe itself differently depending on whether someone is reading the
 * OMS or the spreadsheet.
 *
 * NOT_INVESTIGABLE is spelled out because the row says two things: that the
 * automation could not investigate it, and that a person has to. The short
 * form only said the second.
 */
export const INVESTIGATION_STATUS_LABEL = {
  QUEUED: 'Queued',
  // Not "Queued": a temporary loss is not waiting for the agent, and a reader
  // who saw "Queued" on it for weeks would reasonably file a bug.
  TEMPORARY: 'Temporary - Awaiting Recovery',
  RECOVERED: 'Recovered - No Financial Impact',
  INVESTIGATING: 'Investigating',
  INVESTIGATED: 'Investigated',
  APPROVED: 'Approved',
  NOT_INVESTIGABLE: 'Not Investigable - Needs manual handling',
  FAILED: 'Failed',
  VOIDED: 'Voided',
};

export const STATUS_OPTIONS = [
  // First in the list on purpose: these are the rows somebody still has to come
  // back to, which is what a filter is usually being opened to find.
  { value: LOSS_STATUSES.TEMPORARY, label: INVESTIGATION_STATUS_LABEL.TEMPORARY },
  { value: LOSS_STATUSES.RECOVERED, label: INVESTIGATION_STATUS_LABEL.RECOVERED },
  { value: LOSS_STATUSES.QUEUED, label: INVESTIGATION_STATUS_LABEL.QUEUED },
  { value: LOSS_STATUSES.INVESTIGATING, label: INVESTIGATION_STATUS_LABEL.INVESTIGATING },
  { value: LOSS_STATUSES.INVESTIGATED, label: INVESTIGATION_STATUS_LABEL.INVESTIGATED },
  { value: LOSS_STATUSES.APPROVED, label: INVESTIGATION_STATUS_LABEL.APPROVED },
  { value: LOSS_STATUSES.NOT_INVESTIGABLE, label: INVESTIGATION_STATUS_LABEL.NOT_INVESTIGABLE },
  { value: LOSS_STATUSES.FAILED, label: INVESTIGATION_STATUS_LABEL.FAILED },
  { value: LOSS_STATUSES.VOIDED, label: INVESTIGATION_STATUS_LABEL.VOIDED },
];

// One label per status, for every screen that shows one. Derived from
// STATUS_OPTIONS rather than written out again: the modal and the queue each kept
// their own literal copy and had already drifted — NOT_INVESTIGABLE read "Needs
// manual handling" in one and "Manual" in the other — so the same report
// described itself differently depending on which screen the agent was
// looking at.
//
// REPORTED is added here and deliberately absent from STATUS_OPTIONS: it is the
// instant before the fan-out enqueues a report, worth labelling but not worth
// offering as a filter.
export const STATUS_LABELS = STATUS_OPTIONS.reduce((labels, option) => ({ ...labels, [option.value]: option.label }), {
  [LOSS_STATUSES.REPORTED]: INVESTIGATION_STATUS_LABEL.QUEUED,
});

// status-tag colour class: success / pending / error / default (grey).
export const STATUS_TAG = {
  [LOSS_STATUSES.REPORTED]: 'default',
  // Amber: it is open and someone has to come back to it.
  [LOSS_STATUSES.TEMPORARY]: 'pending',
  // Green: closed, and it cost nothing in the end.
  [LOSS_STATUSES.RECOVERED]: 'success',
  [LOSS_STATUSES.QUEUED]: 'default',
  [LOSS_STATUSES.INVESTIGATING]: 'pending',
  [LOSS_STATUSES.INVESTIGATED]: 'pending',
  [LOSS_STATUSES.APPROVED]: 'success',
  [LOSS_STATUSES.NOT_INVESTIGABLE]: 'error',
  [LOSS_STATUSES.FAILED]: 'error',
  [LOSS_STATUSES.VOIDED]: 'default',
};

export const SEVERITIES = {
  // Amount is optional; an unquantified loss cannot be banded. Reporting it as
  // LOW would understate it — the backend returns UNSPECIFIED for a null amount.
  UNSPECIFIED: 'UNSPECIFIED',
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

export const SEVERITY_OPTIONS = [
  { value: SEVERITIES.UNSPECIFIED, label: 'Unspecified' },
  { value: SEVERITIES.LOW, label: 'Low' },
  { value: SEVERITIES.MEDIUM, label: 'Medium' },
  { value: SEVERITIES.HIGH, label: 'High' },
  { value: SEVERITIES.CRITICAL, label: 'Critical' },
];

// Mirrors SEVERITY_EMOJI in the backend constants so the OMS chip, the Chat alert
// and the register all read the same.
export const SEVERITY_EMOJI = {
  [SEVERITIES.UNSPECIFIED]: '⚪',
  [SEVERITIES.LOW]: '🟢',
  [SEVERITIES.MEDIUM]: '🟡',
  [SEVERITIES.HIGH]: '🟠',
  [SEVERITIES.CRITICAL]: '🔴',
};

export const SEVERITY_COLORS = {
  [SEVERITIES.UNSPECIFIED]: '#868e96',
  [SEVERITIES.LOW]: '#28a745',
  [SEVERITIES.MEDIUM]: '#f0ad4e',
  [SEVERITIES.HIGH]: '#fd7e14',
  [SEVERITIES.CRITICAL]: '#dc3545',
};

export const CONFIDENCE = {
  CONFIRMED: 'CONFIRMED',
  SUSPECTED: 'SUSPECTED',
};

export const CONFIDENCE_OPTIONS = [
  { value: CONFIDENCE.CONFIRMED, label: 'Confirmed' },
  { value: CONFIDENCE.SUSPECTED, label: 'Suspected' },
];

export const AUTHOR_KIND = {
  AGENT: 'AGENT',
  HUMAN: 'HUMAN',
};

// Mirrors LOSS_DESCRIPTION_MIN_LENGTH / _MAX_LENGTH in the backend constants.
// Fixed on both sides rather than configurable, so a description that submits on
// staging cannot be rejected by production. The server is authoritative; these
// only give immediate feedback instead of a round trip.
//
// The floor is deliberately low — it catches an empty or accidental submit, not
// a thin one. At 40 it blocked the Report Loss button until the reporter padded
// to length, and padding is worse input to the investigation than a short
// honest sentence. See the note beside the backend constant.
export const DESCRIPTION_MIN_LENGTH = 10;
export const DESCRIPTION_MAX_LENGTH = 2000;
export const REJECT_REASON_MIN_LENGTH = 20;

// Mirrors investigation_rules.py, which the amend endpoint validates against. A
// reviewer editing an investigation is held to the same grammar as the agent —
// otherwise one free-text category from a human breaks the grouping the whole
// register is read by. The server stays authoritative; these give the feedback
// before the round trip, which matters on a form this long.
export const CATEGORY_SEPARATOR = ' - ';
// The label is stored as one string and is now edited as one. It was three boxes
// while the server required exactly three parts; that requirement is gone, and
// three boxes outlived it — they made a reviewer invent a Subject and a Root
// cause before they could approve a finding whose honest label was
// `Payment Retry Did Not Retain Voucher`, which is the padding the rule change
// set out to remove.
export const CATEGORY_MAX_PARTS = 3;
export const CATEGORY_PART_MAX_LENGTH = 60;
// The size rules, and the whole of what Edit & Approve is checked against —
// mirroring validate_bullet_sizes on the server rather than validate_bullets.
//
// These had drifted in both directions at once. The cap stayed at 5 after the
// server raised it to 8, so a reviewer told by the server to "split it" was
// then refused by this form for having too many lines; and there was no
// ceiling at all, so a 400-character bullet submitted cleanly and came back as
// a bare "Invalid input params." with the real reason buried in a field the
// toast never shows.
export const MIN_BULLETS = 3;
export const MAX_BULLETS = 8;
export const MIN_BULLET_LENGTH = 40;
// A finding that needs more than this is two findings, and two findings in one
// register row cannot be grouped or corrected later.
export const MAX_BULLET_LENGTH = 300;

/**
 * The findings, as one block of text: one finding per line.
 *
 * A leading bullet glyph is stripped the way the server's own normaliser does,
 * so a reviewer who pastes a bulleted list from the investigation above does not
 * end up storing the glyphs.
 */
export const splitFindings = (text) =>
  (text || '')
    .split('\n')
    .map((line) => line.replace(/^\s*[•\-*]\s*/, '').trim())
    .filter(Boolean);

/** The stored findings, back into one editable block. */
export const joinFindings = (findings) => (findings || []).join('\n');

/**
 * Validate an edited investigation, returning `{ field: message }`.
 *
 * Deliberately does NOT mirror two of the server's rules: the root-cause markers
 * and the check that rejects a file path or an internal field name in a finding.
 * Both live in investigation_rules.py as patterns that would rot silently here.
 * The server rejects with a readable message and the toast shows it — better a
 * rare extra round trip than a client that disagrees with the API about what is
 * valid. What is mirrored below is only what is stable: how many parts, how long
 * each is, and that a label is capitalised.
 */
export const validateInvestigationDraft = ({ category, findings, confidence }) => {
  const errors = {};

  const label = (category || '').trim();
  const parts = label.split(CATEGORY_SEPARATOR).map((part) => part.trim());
  const tooLong = parts.find((part) => part.length > CATEGORY_PART_MAX_LENGTH);
  // Capitalisation is what keeps a one-part label a label. It became the load
  // bearing check the moment `Payment Retry Did Not Retain Voucher` became a
  // legal category — without it, so is `the addon api broke somewhere`.
  const lowercase = parts.find((part) => part && !/^[A-Z0-9]/.test(part));

  if (!label) {
    errors.category = 'A category is required.';
  } else if (parts.length > CATEGORY_MAX_PARTS) {
    errors.category = `At most ${CATEGORY_MAX_PARTS} parts separated by “${CATEGORY_SEPARATOR.trim()}”.`;
  } else if (parts.some((part) => !part)) {
    errors.category = 'A part is empty — remove the stray separator.';
  } else if (tooLong) {
    errors.category = `Keep each part under ${CATEGORY_PART_MAX_LENGTH} characters — it is a label, not prose.`;
  } else if (lowercase) {
    errors.category = `“${lowercase}” starts lowercase — a label is capitalised, prose is not.`;
  }

  const lines = splitFindings(findings);
  const short = lines.findIndex((line) => line.length < MIN_BULLET_LENGTH);
  const long = lines.findIndex((line) => line.length > MAX_BULLET_LENGTH);
  if (lines.length < MIN_BULLETS || lines.length > MAX_BULLETS) {
    errors.findings = `Give ${MIN_BULLETS} to ${MAX_BULLETS} findings, one per line — this has ${lines.length}.`;
  } else if (short !== -1) {
    errors.findings = `Line ${short + 1} needs ${
      MIN_BULLET_LENGTH - lines[short].length
    } more characters — too short to carry an explanation.`;
  } else if (long !== -1) {
    // Says how far over, and what to do about it. The server's own wording is
    // "that is more than one finding; split it" — same rule, same remedy.
    errors.findings = `Line ${long + 1} is ${
      lines[long].length - MAX_BULLET_LENGTH
    } characters over the ${MAX_BULLET_LENGTH} limit — that is more than one finding; split it.`;
  }

  if (!confidence) errors.confidence = 'Pick a confidence.';

  return errors;
};

export const buildOrderDetailPath = (orderId) => `${ROUTE_PREFIX}/admin-panel/booking-summary/${orderId}`;

export const buildLossQueuePath = () => `${ROUTE_PREFIX}/admin-panel/losses`;

export const formatAmount = (amount) => {
  // null/undefined must render blank, NOT "PKR 0.00". `Number(null)` is 0, so a
  // bare Number() coercion would invent a zero amount in a financial UI — the one
  // place a plausible-looking wrong number is worse than an obvious gap.
  if (amount === null || amount === undefined || amount === '') return '';
  const value = Number(amount);
  if (Number.isNaN(value)) return String(amount);
  return `PKR ${value.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// A report is reviewable once the investigator has filed something and nobody has
// signed it off yet. `canApprove` from the API additionally enforces separation of
// duty — this only decides whether the actions are relevant at all.
export const isAwaitingReview = (report) =>
  report?.status === LOSS_STATUSES.INVESTIGATED && !report?.investigation?.approvedAt;

/**
 * Is this loss awaiting recovery or resolution?
 *
 * Read from *status*, not category, and matching `LossReport.is_temporary` on
 * the backend. The two agree at report time, but a resolved loss keeps no trace
 * of having been temporary in its category — so keying off category would keep
 * offering "Recovered" on a loss that is already being investigated.
 */
export const isTemporary = (report) => report?.status === LOSS_STATUSES.TEMPORARY;

// There is deliberately no `isRecovered` counterpart. Nothing in the UI branches
// on RECOVERED: the status chip is driven by STATUS_TAG, and the two actions are
// hidden by `isTemporary` being false. It existed only to be asserted in a test,
// which made the helper and the test justify each other. Add it back the day a
// component actually needs it — it is one line.
